import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import IntelexFrame from "../intelex/IntelexFrame";
import ArrowIcon from "../intelex/ArrowIcon";
import { confirmAction } from "../intelex/ConfirmationDialog";
import {
  dataItem,
  dataList,
  displayDate,
  intelexRequest,
} from "../intelex/api";

let sequence = 0;
const uniqueKey = (prefix) => {
  sequence += 1;
  return `${prefix}_${Date.now().toString(36)}_${sequence.toString(36)}`;
};
const questionTypes = [
  ["single_choice", "Single choice"],
  ["multiple_choice", "Multiple choice"],
  ["true_false", "True or false"],
  ["short_text", "Auto-graded text"],
  ["written_response", "Written response"],
  ["number", "Number"],
  ["matching", "Matching"],
  ["ordering", "Ordering"],
];
const typeName = (type) =>
  questionTypes.find(([value]) => value === type)?.[1] || "Question";

function freshOption(label = "Option") {
  return { id: uniqueKey("option"), label };
}
function freshQuestion(type = "single_choice") {
  const first = freshOption("Option 1");
  const second = freshOption("Option 2");
  const base = {
    key: uniqueKey("question"),
    type,
    prompt: "New question",
    help: "",
    points: 1,
    required: true,
  };
  if (type === "single_choice")
    return { ...base, options: [first, second], correctOption: first.id };
  if (type === "multiple_choice")
    return { ...base, options: [first, second], correctOptions: [first.id] };
  if (type === "true_false") return { ...base, correctBoolean: true };
  if (type === "short_text")
    return {
      ...base,
      acceptedAnswers: ["Correct answer"],
      caseSensitive: false,
    };
  if (type === "written_response")
    return { ...base, points: 0, multiline: true };
  if (type === "number") return { ...base, correctNumber: 0, tolerance: 0 };
  if (type === "matching")
    return {
      ...base,
      pairs: [
        {
          leftId: uniqueKey("left"),
          rightId: uniqueKey("right"),
          left: "Item 1",
          right: "Match 1",
        },
        {
          leftId: uniqueKey("left"),
          rightId: uniqueKey("right"),
          left: "Item 2",
          right: "Match 2",
        },
      ],
    };
  return { ...base, options: [first, second] };
}
function freshSection() {
  return {
    key: uniqueKey("section"),
    title: "New section",
    description: "",
    randomize: false,
    selectionLimit: null,
    questions: [freshQuestion()],
  };
}
function freshExam() {
  return {
    title: "",
    description: "",
    instructions: "Complete the assessment without external assistance.",
    status: "draft",
    isEntryExam: true,
    passingPercentage: 80,
    timeLimitMinutes: 45,
    allowRetake: true,
    retakeDelayHours: 48,
    maxAttempts: 3,
    requireFullscreen: true,
    maxViolations: 3,
    autoFailOnViolation: true,
    sections: [freshSection()],
  };
}
function normalizeExam(exam) {
  return {
    ...freshExam(),
    ...exam,
    sections: (exam.sections || []).map((section) => ({
      ...section,
      key: section.key || uniqueKey("section"),
      questions: (section.questions || []).map((question) => ({
        ...question,
        key: question.key || uniqueKey("question"),
      })),
    })),
  };
}
function moveItem(items, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}
function answerText(question) {
  const value = question.answer;
  if (value === null || value === undefined || value === "") return "No answer";
  const options = question.config?.options || [];
  const optionLabel = (id) =>
    options.find((option) => String(option.id) === String(id))?.label ||
    String(id);
  if (question.type === "single_choice" || question.type === "true_false")
    return optionLabel(value);
  if (question.type === "multiple_choice" && Array.isArray(value))
    return value.map(optionLabel).join(", ");
  if (question.type === "ordering" && Array.isArray(value))
    return value.map(optionLabel).join(" → ");
  if (question.type === "matching" && typeof value === "object") {
    const left = question.config?.left || [];
    const right = question.config?.right || [];
    return Object.entries(value)
      .map(([leftId, rightId]) => {
        const leftLabel =
          left.find((item) => String(item.id) === String(leftId))?.label ||
          leftId;
        const rightLabel =
          right.find((item) => String(item.id) === String(rightId))?.label ||
          rightId;
        return `${leftLabel} → ${rightLabel}`;
      })
      .join("; ");
  }
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const integrityEventDetails = {
  started: {
    title: "Exam session started",
    description:
      "The server created the timed attempt and began recording activity.",
    tone: "session",
  },
  cancelled: {
    title: "Attempt cancelled by applicant",
    description:
      "The applicant deliberately ended the exam. The cancelled attempt counts toward the configured attempt limit.",
    tone: "violation",
  },
  visibility_hidden: {
    title: "Exam page was hidden",
    description:
      "The browser reported that the exam page was no longer visible. This commonly means a tab change, browser minimisation or device lock; the browser cannot identify what was viewed.",
    tone: "violation",
    restoredBy: "visibility_visible",
  },
  visibility_visible: {
    title: "Exam page became visible",
    description:
      "The applicant returned to the exam page or made it visible again.",
    tone: "return",
  },
  window_blur: {
    title: "Exam window lost focus",
    description:
      "The exam browser window stopped being the active window. This can result from selecting another application, another window, the address bar or browser controls.",
    tone: "violation",
    restoredBy: "window_focus",
  },
  window_focus: {
    title: "Exam window regained focus",
    description: "The applicant made the exam browser window active again.",
    tone: "return",
  },
  fullscreen_exit: {
    title: "Fullscreen mode was exited",
    description:
      "The browser confirmed that the protected fullscreen view was closed.",
    tone: "violation",
    restoredBy: "fullscreen_enter",
  },
  fullscreen_enter: {
    title: "Fullscreen mode was entered",
    description:
      "The browser confirmed that the exam returned to fullscreen mode.",
    tone: "return",
  },
  copy_attempt: {
    title: "Copy action was attempted",
    description: "A browser copy action was blocked while the exam was open.",
    tone: "violation",
  },
  cut_attempt: {
    title: "Cut action was attempted",
    description: "A browser cut action was blocked while the exam was open.",
    tone: "violation",
  },
  paste_attempt: {
    title: "Paste action was attempted",
    description: "A browser paste action was blocked while the exam was open.",
    tone: "violation",
  },
  context_menu: {
    title: "Context menu was requested",
    description: "A right-click or equivalent context-menu action was blocked.",
    tone: "violation",
  },
  screenshot_attempt: {
    title: "Screenshot command was attempted",
    description:
      "A browser-recognised screenshot command was blocked and recorded while the exam was open.",
    tone: "violation",
  },
  print_attempt: {
    title: "Print command was attempted",
    description:
      "A print command was blocked and the printable exam content was concealed.",
    tone: "violation",
  },
  page_exit: {
    title: "Page exit was attempted",
    description:
      "The browser began leaving, closing or reloading the exam page. The exact destination is not exposed by the browser.",
    tone: "violation",
    restoredBy: "reconnected",
  },
  reconnected: {
    title: "Exam session reconnected",
    description:
      "The exam page contacted the server again after an interruption.",
    tone: "return",
  },
  submitted: {
    title: "Exam was submitted",
    description:
      "The server received the final answers and closed the attempt.",
    tone: "session",
  },
};

function integrityDuration(events, index, restoredBy) {
  if (!restoredBy) return "";
  const start = new Date(events[index].createdAt).getTime();
  const recovery = events
    .slice(index + 1)
    .find((event) => event.type === restoredBy);
  if (!recovery || !Number.isFinite(start)) return "No return event recorded";
  const seconds = Math.max(
    0,
    Math.round((new Date(recovery.createdAt).getTime() - start) / 1000),
  );
  if (seconds < 60)
    return `Returned after ${seconds} second${seconds === 1 ? "" : "s"}`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `Returned after ${minutes}m ${remainder}s`;
}

function IntegrityTimeline({ events = [], violationCount = 0 }) {
  const heartbeatCount = events.filter(
    (event) => event.type === "heartbeat",
  ).length;
  const timeline = events.filter((event) => event.type !== "heartbeat");
  return (
    <section className="intelex-attempt-events">
      <header>
        <div>
          <h3>Integrity timeline</h3>
          <p>
            These are browser signals, not a recording of the applicant's
            screen. Review their sequence and duration before making a decision.
          </p>
        </div>
        <div className="intelex-integrity-summary">
          <span className={violationCount ? "warning" : "clear"}>
            {violationCount} violation{violationCount === 1 ? "" : "s"}
          </span>
          <span>{heartbeatCount} connectivity checks</span>
        </div>
      </header>
      {timeline.length ? (
        <div className="intelex-integrity-timeline">
          {timeline.map((event, index) => {
            const information = integrityEventDetails[event.type] || {
              title: event.type.replaceAll("_", " "),
              description: "A system activity event was recorded.",
              tone: "session",
            };
            const duration = integrityDuration(
              timeline,
              index,
              information.restoredBy,
            );
            return (
              <article
                className={`is-${information.tone}`}
                key={`${event.createdAt}-${event.type}-${index}`}
              >
                <i aria-hidden="true" />
                <time dateTime={event.createdAt}>
                  {displayDate(event.createdAt)}
                </time>
                <div>
                  <strong>{information.title}</strong>
                  <p>{information.description}</p>
                  {duration && <small>{duration}</small>}
                  {typeof event.detail?.countedTowardsLimit === "boolean" && (
                    <small>
                      {event.detail.countedTowardsLimit
                        ? "Counted as one focus violation"
                        : [
                              "copy_attempt",
                              "cut_attempt",
                              "paste_attempt",
                              "context_menu",
                              "screenshot_attempt",
                              "print_attempt",
                            ].includes(event.type)
                          ? "Recorded and blocked — not counted as a focus violation"
                          : "Recorded only — grouped with the same focus-loss incident"}
                    </small>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p>No integrity activity was recorded for this attempt.</p>
      )}
    </section>
  );
}

function Modal({ title, subtitle, onClose, children, wide = false }) {
  useEffect(() => {
    const close = (event) => {
      if (event.key === "Escape") onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", close);
    };
  }, [onClose]);
  return (
    <div
      className="intelex-builder-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`intelex-builder-modal${wide ? " is-wide" : ""}`}
        role="dialog"
        aria-modal="true"
      >
        <header>
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function SettingsEditor({ exam, onCancel, onSave }) {
  const [draft, setDraft] = useState({ ...exam });
  const set = (key, value) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const submit = (event) => {
    event.preventDefault();
    onSave(draft);
  };
  return (
    <form
      className="intelex-modal-form intelex-exam-settings-form"
      onSubmit={submit}
    >
      <div className="intelex-modal-form-grid">
        <label>
          <span>Exam title</span>
          <input
            autoFocus
            required
            maxLength="180"
            value={draft.title}
            onChange={(event) => set("title", event.target.value)}
          />
        </label>
        <label>
          <span>Availability</span>
          <select
            value={draft.status}
            onChange={(event) => set("status", event.target.value)}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </label>
        <label className="wide">
          <span>Description</span>
          <textarea
            rows="3"
            maxLength="2000"
            value={draft.description}
            onChange={(event) => set("description", event.target.value)}
          />
        </label>
        <label className="wide">
          <span>Instructions shown before starting</span>
          <textarea
            required
            rows="5"
            maxLength="10000"
            value={draft.instructions}
            onChange={(event) => set("instructions", event.target.value)}
          />
        </label>
        <label>
          <span>Passing grade (%)</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={draft.passingPercentage}
            onChange={(event) =>
              set("passingPercentage", Number(event.target.value))
            }
          />
        </label>
        <label>
          <span>Time limit (minutes)</span>
          <input
            type="number"
            min="1"
            max="300"
            value={draft.timeLimitMinutes}
            onChange={(event) =>
              set("timeLimitMinutes", Number(event.target.value))
            }
          />
        </label>
        <label>
          <span>Retake delay (hours)</span>
          <input
            type="number"
            min="0"
            max="8760"
            disabled={!draft.allowRetake}
            value={draft.retakeDelayHours}
            onChange={(event) =>
              set("retakeDelayHours", Number(event.target.value))
            }
          />
        </label>
        <label>
          <span>Maximum attempts</span>
          <input
            type="number"
            min="1"
            max="100"
            disabled={!draft.allowRetake}
            value={draft.maxAttempts ?? ""}
            placeholder="Unlimited"
            onChange={(event) =>
              set(
                "maxAttempts",
                event.target.value === "" ? null : Number(event.target.value),
              )
            }
          />
        </label>
        <label>
          <span>Allowed focus violations</span>
          <input
            type="number"
            min="0"
            max="100"
            value={draft.maxViolations}
            onChange={(event) =>
              set("maxViolations", Number(event.target.value))
            }
          />
        </label>
        <label className="intelex-modal-checkbox">
          <input
            type="checkbox"
            checked={draft.isEntryExam}
            onChange={(event) => set("isEntryExam", event.target.checked)}
          />
          <span>Use as the entry exam</span>
        </label>
        <label className="intelex-modal-checkbox">
          <input
            type="checkbox"
            checked={draft.allowRetake}
            onChange={(event) => set("allowRetake", event.target.checked)}
          />
          <span>Allow another attempt after failure</span>
        </label>
        <label className="intelex-modal-checkbox">
          <input
            type="checkbox"
            checked={draft.requireFullscreen}
            onChange={(event) => set("requireFullscreen", event.target.checked)}
          />
          <span>Require fullscreen</span>
        </label>
        <label className="intelex-modal-checkbox">
          <input
            type="checkbox"
            checked={draft.autoFailOnViolation}
            onChange={(event) =>
              set("autoFailOnViolation", event.target.checked)
            }
          />
          <span>Automatically fail after the allowed event count</span>
        </label>
      </div>
      <footer>
        <button
          type="button"
          className="intelex-secondary-button"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="submit" className="intelex-primary-button">
          Apply settings
        </button>
      </footer>
    </form>
  );
}

function SectionEditor({ section, onCancel, onSave }) {
  const [draft, setDraft] = useState({ ...section });
  const set = (key, value) =>
    setDraft((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="intelex-modal-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(draft);
      }}
    >
      <div className="intelex-modal-form-grid">
        <label>
          <span>Section title</span>
          <input
            autoFocus
            required
            maxLength="160"
            value={draft.title}
            onChange={(event) => set("title", event.target.value)}
          />
        </label>
        <label className="wide">
          <span>Description</span>
          <textarea
            rows="3"
            maxLength="1000"
            value={draft.description}
            onChange={(event) => set("description", event.target.value)}
          />
        </label>
        <label className="intelex-modal-checkbox wide">
          <input
            type="checkbox"
            checked={draft.randomize}
            onChange={(event) => set("randomize", event.target.checked)}
          />
          <span>Randomize and cycle this question bank between attempts</span>
        </label>
        <label>
          <span>Questions displayed</span>
          <input
            type="number"
            min="1"
            max={Math.max(1, draft.questions.length)}
            value={draft.selectionLimit ?? ""}
            placeholder={`All ${draft.questions.length}`}
            onChange={(event) =>
              set(
                "selectionLimit",
                event.target.value === "" ? null : Number(event.target.value),
              )
            }
          />
          <small>Leave blank to display every question.</small>
        </label>
      </div>
      <footer>
        <button
          type="button"
          className="intelex-secondary-button"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="submit" className="intelex-primary-button">
          Apply section
        </button>
      </footer>
    </form>
  );
}

function OptionRows({ options, onChange, mode, selected, onSelected }) {
  const change = (index, key, value) =>
    onChange(
      options.map((item, position) =>
        position === index ? { ...item, [key]: value } : item,
      ),
    );
  const add = () =>
    onChange([...options, freshOption(`Option ${options.length + 1}`)]);
  const remove = (index) => {
    const removed = options[index];
    onChange(options.filter((_, position) => position !== index));
    if (mode === "single" && selected === removed.id)
      onSelected(options.find((_, position) => position !== index)?.id || "");
    if (mode === "multiple")
      onSelected((selected || []).filter((id) => id !== removed.id));
  };
  return (
    <div className="intelex-exam-option-editor">
      <div>
        <strong>
          {mode === "ordering" ? "Correct order" : "Answer options"}
        </strong>
        <button type="button" onClick={add}>
          Add option
        </button>
      </div>
      {options.map((option, index) => (
        <article key={option.id}>
          {mode === "single" && (
            <input
              aria-label="Correct answer"
              type="radio"
              name="correct-answer"
              checked={selected === option.id}
              onChange={() => onSelected(option.id)}
            />
          )}
          {mode === "multiple" && (
            <input
              aria-label="Correct answer"
              type="checkbox"
              checked={(selected || []).includes(option.id)}
              onChange={() =>
                onSelected(
                  (selected || []).includes(option.id)
                    ? selected.filter((id) => id !== option.id)
                    : [...(selected || []), option.id],
                )
              }
            />
          )}
          <span>{index + 1}</span>
          <input
            value={option.label}
            maxLength="300"
            onChange={(event) => change(index, "label", event.target.value)}
          />
          <button
            type="button"
            disabled={index === 0}
            onClick={() => onChange(moveItem(options, index, -1))}
          >
            Up
          </button>
          <button
            type="button"
            disabled={index === options.length - 1}
            onClick={() => onChange(moveItem(options, index, 1))}
          >
            Down
          </button>
          <button
            type="button"
            className="danger"
            disabled={options.length <= 2}
            onClick={() => remove(index)}
          >
            Remove
          </button>
        </article>
      ))}
    </div>
  );
}

function QuestionEditor({ question, onCancel, onSave }) {
  const [draft, setDraft] = useState({
    ...question,
    options: question.options
      ? question.options.map((item) => ({ ...item }))
      : undefined,
    pairs: question.pairs
      ? question.pairs.map((item) => ({ ...item }))
      : undefined,
  });
  const [error, setError] = useState("");
  const set = (key, value) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const changeType = (type) =>
    setDraft({
      ...freshQuestion(type),
      key: draft.key,
      prompt: draft.prompt,
      help: draft.help,
      required: draft.required,
      type,
    });
  const submit = (event) => {
    event.preventDefault();
    if (!draft.prompt.trim())
      return setError("Enter the question shown to pilots.");
    if (draft.type === "multiple_choice" && !draft.correctOptions?.length)
      return setError("Select at least one correct answer.");
    if (
      draft.type === "short_text" &&
      !draft.acceptedAnswers?.some((item) => item.trim())
    )
      return setError("Add at least one accepted answer.");
    if (
      draft.type === "matching" &&
      draft.pairs?.some((pair) => !pair.left.trim() || !pair.right.trim())
    )
      return setError("Complete both sides of every matching pair.");
    onSave(draft);
  };
  const changePair = (index, key, value) =>
    set(
      "pairs",
      draft.pairs.map((pair, position) =>
        position === index ? { ...pair, [key]: value } : pair,
      ),
    );
  return (
    <form
      className="intelex-modal-form intelex-question-editor"
      onSubmit={submit}
    >
      <div className="intelex-type-selector">
        <strong>Question design</strong>
        <div>
          {questionTypes.map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={draft.type === value ? "selected" : ""}
              onClick={() => changeType(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="intelex-modal-form-grid">
        <label className="wide">
          <span>Question</span>
          <textarea
            autoFocus
            required
            rows="4"
            maxLength="2000"
            value={draft.prompt}
            onChange={(event) => set("prompt", event.target.value)}
          />
        </label>
        <label className="wide">
          <span>Optional guidance</span>
          <textarea
            rows="2"
            maxLength="500"
            value={draft.help}
            onChange={(event) => set("help", event.target.value)}
          />
        </label>
        {draft.type !== "written_response" && (
          <label>
            <span>Points</span>
            <input
              type="number"
              min="0.01"
              max="100"
              step="0.01"
              value={draft.points}
              onChange={(event) => set("points", Number(event.target.value))}
            />
          </label>
        )}
        <label className="intelex-modal-checkbox">
          <input
            type="checkbox"
            checked={draft.required !== false}
            onChange={(event) => set("required", event.target.checked)}
          />
          <span>Answer is required</span>
        </label>
      </div>
      {draft.type === "single_choice" && (
        <OptionRows
          options={draft.options}
          onChange={(value) => set("options", value)}
          mode="single"
          selected={draft.correctOption}
          onSelected={(value) => set("correctOption", value)}
        />
      )}
      {draft.type === "multiple_choice" && (
        <OptionRows
          options={draft.options}
          onChange={(value) => set("options", value)}
          mode="multiple"
          selected={draft.correctOptions}
          onSelected={(value) => set("correctOptions", value)}
        />
      )}
      {draft.type === "ordering" && (
        <OptionRows
          options={draft.options}
          onChange={(value) => set("options", value)}
          mode="ordering"
        />
      )}
      {draft.type === "true_false" && (
        <fieldset className="intelex-exam-boolean">
          <legend>Correct answer</legend>
          <label>
            <input
              type="radio"
              name="boolean"
              checked={draft.correctBoolean === true}
              onChange={() => set("correctBoolean", true)}
            />{" "}
            True
          </label>
          <label>
            <input
              type="radio"
              name="boolean"
              checked={draft.correctBoolean === false}
              onChange={() => set("correctBoolean", false)}
            />{" "}
            False
          </label>
        </fieldset>
      )}
      {draft.type === "short_text" && (
        <div className="intelex-exam-text-key">
          <label>
            <span>Accepted answers, one per line</span>
            <textarea
              rows="5"
              value={(draft.acceptedAnswers || []).join("\n")}
              onChange={(event) =>
                set("acceptedAnswers", event.target.value.split("\n"))
              }
            />
          </label>
          <label className="intelex-modal-checkbox">
            <input
              type="checkbox"
              checked={draft.caseSensitive}
              onChange={(event) => set("caseSensitive", event.target.checked)}
            />
            <span>Answers are case-sensitive</span>
          </label>
        </div>
      )}
      {draft.type === "written_response" && (
        <div className="intelex-exam-text-key">
          <p>
            This response is stored for manual review and does not affect
            the percentage score.
          </p>
          <label className="intelex-modal-checkbox">
            <input
              type="checkbox"
              checked={draft.multiline !== false}
              onChange={(event) => set("multiline", event.target.checked)}
            />
            <span>Use a large multi-line answer box</span>
          </label>
        </div>
      )}
      {draft.type === "number" && (
        <div className="intelex-modal-rules">
          <strong>Correct number</strong>
          <label>
            <span>Answer</span>
            <input
              type="number"
              step="any"
              value={draft.correctNumber}
              onChange={(event) =>
                set("correctNumber", Number(event.target.value))
              }
            />
          </label>
          <label>
            <span>Accepted tolerance (+/-)</span>
            <input
              type="number"
              min="0"
              step="any"
              value={draft.tolerance}
              onChange={(event) => set("tolerance", Number(event.target.value))}
            />
          </label>
        </div>
      )}
      {draft.type === "matching" && (
        <div className="intelex-exam-pair-editor">
          <div>
            <strong>Matching pairs</strong>
            <button
              type="button"
              onClick={() =>
                set("pairs", [
                  ...draft.pairs,
                  {
                    leftId: uniqueKey("left"),
                    rightId: uniqueKey("right"),
                    left: `Item ${draft.pairs.length + 1}`,
                    right: `Match ${draft.pairs.length + 1}`,
                  },
                ])
              }
            >
              Add pair
            </button>
          </div>
          {draft.pairs.map((pair, index) => (
            <article key={pair.leftId}>
              <span>{index + 1}</span>
              <input
                aria-label="Left item"
                value={pair.left}
                onChange={(event) =>
                  changePair(index, "left", event.target.value)
                }
              />
              <i>matches</i>
              <input
                aria-label="Correct match"
                value={pair.right}
                onChange={(event) =>
                  changePair(index, "right", event.target.value)
                }
              />
              <button
                type="button"
                className="danger"
                disabled={draft.pairs.length <= 2}
                onClick={() =>
                  set(
                    "pairs",
                    draft.pairs.filter((_, position) => position !== index),
                  )
                }
              >
                Remove
              </button>
            </article>
          ))}
        </div>
      )}
      {error && (
        <div className="intelex-alert" role="alert">
          {error}
        </div>
      )}
      <footer>
        <button
          type="button"
          className="intelex-secondary-button"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="submit" className="intelex-primary-button">
          Apply question
        </button>
      </footer>
    </form>
  );
}

function QuestionPreview({ question, onChange }) {
  const options = question.options || [];
  const set = (key, value) => onChange({ ...question, [key]: value });
  const changeOption = (index, label) =>
    set(
      "options",
      options.map((option, position) =>
        position === index ? { ...option, label } : option,
      ),
    );
  const changePair = (index, key, value) =>
    set(
      "pairs",
      question.pairs.map((pair, position) =>
        position === index ? { ...pair, [key]: value } : pair,
      ),
    );
  return (
    <div className="intelex-exam-question-preview intelex-inline-edit-preview">
      <div>
        <span>{typeName(question.type)}</span>
        <label>
          <small>Points</small>
          <input
            type="number"
            min="0.01"
            max="100"
            step="0.01"
            disabled={question.type === "written_response"}
            value={question.points}
            onChange={(event) => set("points", Number(event.target.value))}
          />
        </label>
      </div>
      <textarea
        className="intelex-inline-title"
        rows="2"
        maxLength="2000"
        aria-label="Question text"
        value={question.prompt}
        onChange={(event) => set("prompt", event.target.value)}
      />
      <input
        className="intelex-inline-help"
        maxLength="500"
        aria-label="Question guidance"
        value={question.help || ""}
        placeholder="Optional guidance"
        onChange={(event) => set("help", event.target.value)}
      />
      {["single_choice", "multiple_choice", "ordering"].includes(
        question.type,
      ) && (
        <div className="intelex-exam-preview-options">
          {options.map((option, index) => (
            <label key={option.id}>
              {question.type === "single_choice" ? (
                <input
                  type="radio"
                  name={`inline-${question.key}`}
                  checked={question.correctOption === option.id}
                  onChange={() => set("correctOption", option.id)}
                />
              ) : question.type === "multiple_choice" ? (
                <input
                  type="checkbox"
                  checked={(question.correctOptions || []).includes(option.id)}
                  onChange={() =>
                    set(
                      "correctOptions",
                      (question.correctOptions || []).includes(option.id)
                        ? question.correctOptions.filter(
                            (id) => id !== option.id,
                          )
                        : [...(question.correctOptions || []), option.id],
                    )
                  }
                />
              ) : (
                <i>{index + 1}</i>
              )}
              <input
                maxLength="300"
                value={option.label}
                aria-label={`Option ${index + 1}`}
                onChange={(event) => changeOption(index, event.target.value)}
              />
            </label>
          ))}
        </div>
      )}
      {question.type === "true_false" && (
        <div className="intelex-exam-preview-options">
          <label>
            <input
              type="radio"
              name={`inline-${question.key}`}
              checked={question.correctBoolean === true}
              onChange={() => set("correctBoolean", true)}
            />
            <span>True</span>
          </label>
          <label>
            <input
              type="radio"
              name={`inline-${question.key}`}
              checked={question.correctBoolean === false}
              onChange={() => set("correctBoolean", false)}
            />
            <span>False</span>
          </label>
        </div>
      )}
      {question.type === "short_text" && (
        <textarea
          className="intelex-inline-answer-key"
          rows="2"
          aria-label="Accepted answers"
          value={(question.acceptedAnswers || []).join("\n")}
          onChange={(event) =>
            set("acceptedAnswers", event.target.value.split("\n"))
          }
          placeholder="Accepted answers one per line"
        />
      )}
      {question.type === "written_response" &&
        (question.multiline !== false ? (
          <textarea
            className="intelex-exam-written-answer"
            disabled
            rows="4"
            placeholder="Applicant enters a written response"
          />
        ) : (
          <input
            className="intelex-exam-written-input"
            disabled
            placeholder="Applicant enters a response"
          />
        ))}
      {question.type === "number" && (
        <div className="intelex-inline-number-key">
          <label>
            <span>Correct answer</span>
            <input
              type="number"
              step="any"
              value={question.correctNumber}
              onChange={(event) =>
                set("correctNumber", Number(event.target.value))
              }
            />
          </label>
          <label>
            <span>Tolerance</span>
            <input
              type="number"
              min="0"
              step="any"
              value={question.tolerance}
              onChange={(event) => set("tolerance", Number(event.target.value))}
            />
          </label>
        </div>
      )}
      {question.type === "matching" && (
        <div className="intelex-exam-preview-matches">
          {question.pairs.map((pair, index) => (
            <label key={pair.leftId}>
              <input
                value={pair.left}
                aria-label={`Matching item ${index + 1}`}
                onChange={(event) =>
                  changePair(index, "left", event.target.value)
                }
              />
              <input
                value={pair.right}
                aria-label={`Correct match ${index + 1}`}
                onChange={(event) =>
                  changePair(index, "right", event.target.value)
                }
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionDisplayPreview({ question }) {
  const options = question.options || [];
  return (
    <div className="intelex-exam-question-preview intelex-question-display-preview">
      <div>
        <span>{typeName(question.type)}</span>
        <span>
          {question.type === "written_response"
            ? "Manual review"
            : `${question.points} ${Number(question.points) === 1 ? "point" : "points"}`}
        </span>
      </div>
      <h3>{question.prompt || "Untitled question"}</h3>
      {question.help && <p>{question.help}</p>}
      {["single_choice", "multiple_choice", "ordering"].includes(
        question.type,
      ) && (
        <div className="intelex-exam-preview-options">
          {options.map((option, index) => (
            <span key={option.id || index}>
              <i>{question.type === "ordering" ? index + 1 : ""}</i>
              {option.label}
            </span>
          ))}
        </div>
      )}
      {question.type === "true_false" && (
        <div className="intelex-exam-preview-options">
          <span>
            <i />
            True
          </span>
          <span>
            <i />
            False
          </span>
        </div>
      )}
      {question.type === "matching" && (
        <div className="intelex-exam-preview-matches">
          {(question.pairs || []).map((pair, index) => (
            <label key={pair.leftId || index}>
              <span>{pair.left}</span>
              <select disabled>
                <option>Select a match</option>
              </select>
            </label>
          ))}
        </div>
      )}
      {question.type === "written_response" &&
        (question.multiline !== false ? (
          <textarea
            className="intelex-exam-written-answer"
            disabled
            rows="4"
            placeholder="Applicant enters a written response"
          />
        ) : (
          <input
            className="intelex-exam-written-input"
            disabled
            placeholder="Applicant enters a response"
          />
        ))}
      {["short_text", "number"].includes(question.type) && (
        <input disabled placeholder="Applicant enters an answer" />
      )}
    </div>
  );
}

function ExamEditor({ initial, onCancel, onSaved }) {
  const [exam, setExam] = useState(() => normalizeExam(initial));
  const [modal, setModal] = useState(null);
  const [openQuestions, setOpenQuestions] = useState({});
  const [state, setState] = useState({ saving: false, error: "" });
  const questionDisclosureKey = (sectionIndex, questionIndex) =>
    `${sectionIndex}:${questionIndex}`;
  const toggleQuestion = (sectionIndex, questionIndex) => {
    const key = questionDisclosureKey(sectionIndex, questionIndex);
    setOpenQuestions((current) => ({ ...current, [key]: !current[key] }));
  };
  const replaceSection = (index, section) =>
    setExam((current) => ({
      ...current,
      sections: current.sections.map((item, position) =>
        position === index ? section : item,
      ),
    }));
  const addSection = (index) =>
    setExam((current) => {
      const sections = [...current.sections];
      sections.splice(index, 0, freshSection());
      return { ...current, sections };
    });
  const save = async () => {
    setState({ saving: true, error: "" });
    try {
      const path = exam.id
        ? `/api/portal/exams/admin/exams/${exam.id}`
        : "/api/portal/exams/admin/exams";
      await intelexRequest(path, {
        method: exam.id ? "PUT" : "POST",
        body: exam,
      });
      onSaved();
    } catch (error) {
      setState({
        saving: false,
        error: error.message || "The exam could not be saved.",
      });
    }
  };
  const activeSection =
    modal?.sectionIndex === undefined
      ? null
      : exam.sections[modal.sectionIndex];
  const activeQuestion =
    modal?.questionIndex === undefined
      ? null
      : activeSection?.questions[modal.questionIndex];
  return (
    <div className="intelex-main intelex-exam-admin intelex-exam-editor">
      <header className="intelex-builder-heading">
        <div>
          <button
            type="button"
            className="intelex-back-button"
            onClick={onCancel}
          >
            Back to exams
          </button>
          <h1>{exam.id ? `Edit ${exam.title}` : "Create exam"}</h1>
          <p>The canvas below matches the structure applicants will see.</p>
        </div>
        <div>
          <button
            type="button"
            className="intelex-secondary-button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="intelex-primary-button"
            disabled={state.saving}
            onClick={save}
          >
            {state.saving ? "Saving..." : "Save exam"}
          </button>
        </div>
      </header>
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      <section className="intelex-exam-identity">
        <div>
          <span>
            {exam.status === "published"
              ? "Published entry assessment"
              : "Draft assessment"}
          </span>
          <input
            className="intelex-inline-heading"
            maxLength="180"
            aria-label="Exam title"
            value={exam.title}
            placeholder="Untitled exam"
            onChange={(event) =>
              setExam((current) => ({ ...current, title: event.target.value }))
            }
          />
          <textarea
            className="intelex-inline-description"
            rows="2"
            maxLength="2000"
            aria-label="Exam description"
            value={exam.description}
            placeholder="Add an exam description"
            onChange={(event) =>
              setExam((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
          />
        </div>
        <dl>
          <div>
            <dt>Pass</dt>
            <dd>{exam.passingPercentage}%</dd>
          </div>
          <div>
            <dt>Time</dt>
            <dd>{exam.timeLimitMinutes} min</dd>
          </div>
          <div>
            <dt>Retake</dt>
            <dd>
              {exam.allowRetake
                ? `${exam.retakeDelayHours}h delay`
                : "Disabled"}
            </dd>
          </div>
          <div>
            <dt>Security</dt>
            <dd>{exam.maxViolations} events</dd>
          </div>
        </dl>
        <button type="button" onClick={() => setModal({ kind: "settings" })}>
          Edit exam settings
        </button>
      </section>
      <div className="intelex-exam-canvas">
        {exam.sections.map((section, sectionIndex) => (
          <div className="intelex-exam-section-wrap" key={section.key}>
            <section className="intelex-exam-builder-section">
              <header>
                <span>{String(sectionIndex + 1).padStart(2, "0")}</span>
                <div>
                  <input
                    className="intelex-inline-section-title"
                    maxLength="160"
                    aria-label={`Section ${sectionIndex + 1} title`}
                    value={section.title}
                    onChange={(event) =>
                      replaceSection(sectionIndex, {
                        ...section,
                        title: event.target.value,
                      })
                    }
                  />
                  <textarea
                    className="intelex-inline-section-description"
                    rows="2"
                    maxLength="1000"
                    aria-label={`Section ${sectionIndex + 1} description`}
                    value={section.description || ""}
                    placeholder="Optional section instructions"
                    onChange={(event) =>
                      replaceSection(sectionIndex, {
                        ...section,
                        description: event.target.value,
                      })
                    }
                  />
                  <small>
                    {section.randomize
                      ? `${section.selectionLimit || section.questions.length} of ${section.questions.length} shown / rotated by least used`
                      : `${section.questions.length} questions shown in order`}
                  </small>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setModal({ kind: "section", sectionIndex })}
                  >
                    Advanced settings
                  </button>
                  <button
                    type="button"
                    disabled={sectionIndex === 0}
                    onClick={() =>
                      setExam((current) => ({
                        ...current,
                        sections: moveItem(current.sections, sectionIndex, -1),
                      }))
                    }
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    disabled={sectionIndex === exam.sections.length - 1}
                    onClick={() =>
                      setExam((current) => ({
                        ...current,
                        sections: moveItem(current.sections, sectionIndex, 1),
                      }))
                    }
                  >
                    Down
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={exam.sections.length === 1}
                    onClick={async () => {
                      if (
                        !(await confirmAction({
                          title: "Remove exam section",
                          message: `Remove ${section.title}? Its questions will also be removed from this draft.`,
                          confirmLabel: "Remove section",
                          tone: "danger",
                        }))
                      )
                        return;
                      setExam((current) => ({
                        ...current,
                        sections: current.sections.filter(
                          (_, position) => position !== sectionIndex,
                        ),
                      }));
                    }}
                  >
                    Remove
                  </button>
                </div>
              </header>
              <div className="intelex-exam-question-list">
                {section.questions.map((question, questionIndex) => (
                  <article key={question.key}>
                    <div className="intelex-exam-card-actions">
                      <button
                        type="button"
                        className="intelex-editor-disclosure"
                        aria-expanded={Boolean(
                          openQuestions[
                            questionDisclosureKey(sectionIndex, questionIndex)
                          ],
                        )}
                        onClick={() =>
                          toggleQuestion(sectionIndex, questionIndex)
                        }
                      >
                        <span>
                          <strong>Question {questionIndex + 1}</strong>
                          <small>
                            {question.prompt || "Untitled question"}
                          </small>
                        </span>
                        <ArrowIcon direction="down" />
                      </button>
                      {openQuestions[
                        questionDisclosureKey(sectionIndex, questionIndex)
                      ] && (
                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              setModal({
                                kind: "question",
                                sectionIndex,
                                questionIndex,
                              })
                            }
                          >
                            Advanced edit
                          </button>
                          <button
                            type="button"
                            disabled={questionIndex === 0}
                            onClick={() =>
                              replaceSection(sectionIndex, {
                                ...section,
                                questions: moveItem(
                                  section.questions,
                                  questionIndex,
                                  -1,
                                ),
                              })
                            }
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            disabled={
                              questionIndex === section.questions.length - 1
                            }
                            onClick={() =>
                              replaceSection(sectionIndex, {
                                ...section,
                                questions: moveItem(
                                  section.questions,
                                  questionIndex,
                                  1,
                                ),
                              })
                            }
                          >
                            Down
                          </button>
                          <button
                            type="button"
                            className="danger"
                            disabled={section.questions.length === 1}
                            onClick={() =>
                              replaceSection(sectionIndex, {
                                ...section,
                                questions: section.questions.filter(
                                  (_, position) => position !== questionIndex,
                                ),
                              })
                            }
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    {openQuestions[
                      questionDisclosureKey(sectionIndex, questionIndex)
                    ] ? (
                      <QuestionPreview
                        question={question}
                        onChange={(updated) =>
                          replaceSection(sectionIndex, {
                            ...section,
                            questions: section.questions.map(
                              (item, position) =>
                                position === questionIndex ? updated : item,
                            ),
                          })
                        }
                      />
                    ) : (
                      <QuestionDisplayPreview question={question} />
                    )}
                    <button
                      className="intelex-inline-add"
                      type="button"
                      onClick={() => {
                        const questions = [...section.questions];
                        questions.splice(questionIndex + 1, 0, freshQuestion());
                        replaceSection(sectionIndex, { ...section, questions });
                        setModal({
                          kind: "question",
                          sectionIndex,
                          questionIndex: questionIndex + 1,
                        });
                      }}
                    >
                      <span>+</span> Add question below
                    </button>
                  </article>
                ))}
              </div>
            </section>
            <button
              type="button"
              className="intelex-add-section-between"
              onClick={() => addSection(sectionIndex + 1)}
            >
              <span>+</span> Add section below
            </button>
          </div>
        ))}
      </div>
      <div className="intelex-visual-builder-actions">
        <span>
          {exam.sections.length} sections /{" "}
          {exam.sections.reduce(
            (total, section) => total + section.questions.length,
            0,
          )}{" "}
          questions
        </span>
        <div>
          <button
            type="button"
            className="intelex-secondary-button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="intelex-primary-button"
            disabled={state.saving}
            onClick={save}
          >
            {state.saving ? "Saving..." : "Save exam"}
          </button>
        </div>
      </div>
      {modal?.kind === "settings" && (
        <Modal
          title="Exam settings"
          subtitle="Control grading, timing, retakes, publication and security."
          wide
          onClose={() => setModal(null)}
        >
          <SettingsEditor
            exam={exam}
            onCancel={() => setModal(null)}
            onSave={(settings) => {
              setExam(settings);
              setModal(null);
            }}
          />
        </Modal>
      )}
      {modal?.kind === "section" && activeSection && (
        <Modal
          title="Edit section"
          subtitle="Configure its question bank and selection rules."
          onClose={() => setModal(null)}
        >
          <SectionEditor
            section={activeSection}
            onCancel={() => setModal(null)}
            onSave={(section) => {
              replaceSection(modal.sectionIndex, section);
              setModal(null);
            }}
          />
        </Modal>
      )}
      {modal?.kind === "question" && activeQuestion && (
        <Modal
          title={`Edit ${typeName(activeQuestion.type)}`}
          subtitle="Set the question, answer design, correct key and points."
          wide
          onClose={() => setModal(null)}
        >
          <QuestionEditor
            question={activeQuestion}
            onCancel={() => setModal(null)}
            onSave={(question) => {
              replaceSection(modal.sectionIndex, {
                ...activeSection,
                questions: activeSection.questions.map((item, position) =>
                  position === modal.questionIndex ? question : item,
                ),
              });
              setModal(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}

function AttemptDetail({ reference, onClose, onChanged, onResolved }) {
  const [detail, setDetail] = useState(null);
  const [deletion, setDeletion] = useState({
    open: false,
    reason: "",
    working: false,
    error: "",
  });
  const [state, setState] = useState({
    loading: true,
    working: false,
    error: "",
  });
  const load = useCallback(async () => {
    try {
      const path = reference.legacyId
        ? `/api/portal/exams/admin/attempts/${encodeURIComponent(reference.legacyId)}`
        : `/api/portal/exams/admin/attempts/user/${encodeURIComponent(reference.userId)}/${encodeURIComponent(reference.attemptNumber)}`;
      const loaded = dataItem(await intelexRequest(path));
      setDetail(loaded);
      onResolved(loaded);
      setState({ loading: false, working: false, error: "" });
    } catch (error) {
      setState({ loading: false, working: false, error: error.message });
    }
  }, [onResolved, reference.attemptNumber, reference.legacyId, reference.userId]);
  useEffect(() => {
    load();
  }, [load]);
  const retry = async () => {
    setState((current) => ({ ...current, working: true, error: "" }));
    try {
      await intelexRequest(
        `/api/portal/exams/admin/attempts/${detail.id}/retry-approval`,
        { method: "POST" },
      );
      await load();
      onChanged();
    } catch (error) {
      setState((current) => ({
        ...current,
        working: false,
        error: error.message,
      }));
    }
  };
  const deleteAttempt = async (event) => {
    event.preventDefault();
    if (
      !deletion.reason.trim() ||
      !(await confirmAction({
        title: "Delete exam submission",
        message:
          "Permanently delete this submission, its answers and integrity timeline? This does not reverse a registration already approved in vAMSYS.",
        confirmLabel: "Delete submission",
        tone: "danger",
      }))
    )
      return;
    setDeletion((current) => ({ ...current, working: true, error: "" }));
    try {
      await intelexRequest(
        `/api/portal/exams/admin/attempts/${detail.id}`,
        { method: "DELETE", body: { reason: deletion.reason } },
      );
      await onChanged();
      onClose();
    } catch (error) {
      setDeletion((current) => ({
        ...current,
        working: false,
        error: error.message || "The exam submission could not be deleted.",
      }));
    }
  };
  return (
    <Modal
      title="Exam attempt"
      subtitle="Answers, scoring, integrity events and vAMSYS approval."
      wide
      onClose={onClose}
    >
      {state.loading ? (
        <div className="intelex-loading">Loading attempt...</div>
      ) : detail ? (
        <div className="intelex-attempt-detail">
          <section className="intelex-attempt-summary">
            <div>
              <span>Applicant</span>
              <strong>{detail.vamsysUsername || detail.applicant}</strong>
              <small>
                {detail.vamsysUserId
                  ? `vAMSYS user #${detail.vamsysUserId} · ${detail.email}`
                  : `Local account · ${detail.email}`}
              </small>
            </div>
            <div>
              <span>Result</span>
              <strong>
                {detail.status === "void"
                  ? "cancelled"
                  : String(detail.status).replace("_", " ")}
              </strong>
              <small>
                {detail.percentage === null
                  ? "Not scored"
                  : `${detail.percentage}% / ${detail.passingPercentage}% required`}
              </small>
            </div>
            <div>
              <span>Integrity</span>
              <strong>{detail.violationCount}</strong>
              <small>counted focus violations</small>
            </div>
            <div>
              <span>vAMSYS</span>
              <strong>{detail.approvalStatus.replace("_", " ")}</strong>
              <small>Registration #{detail.registrationId || "-"}</small>
            </div>
          </section>
          {state.error && <div className="intelex-alert">{state.error}</div>}
          <div className="intelex-attempt-admin-actions">
            {detail.status === "passed" &&
              detail.approvalStatus === "failed" && (
                <button
                  type="button"
                  className="intelex-primary-button"
                  disabled={state.working}
                  onClick={retry}
                >
                  {state.working ? "Retrying..." : "Retry vAMSYS approval"}
                </button>
              )}
            <button
              type="button"
              className="intelex-danger-text-button"
              disabled={detail.status === "in_progress"}
              onClick={() =>
                setDeletion((current) => ({ ...current, open: true }))
              }
            >
              Delete submission
            </button>
          </div>
          {deletion.open && (
            <form
              className="intelex-attempt-delete-confirm"
              onSubmit={deleteAttempt}
            >
              <div>
                <strong>Delete this exam submission permanently</strong>
                <p>
                  Answers and integrity events will be removed. Any completed
                  vAMSYS approval remains unchanged.
                </p>
              </div>
              <label>
                <span>Deletion reason</span>
                <textarea
                  required
                  rows="3"
                  maxLength="1000"
                  value={deletion.reason}
                  onChange={(event) =>
                    setDeletion((current) => ({
                      ...current,
                      reason: event.target.value,
                    }))
                  }
                  placeholder="Record why this submission is being deleted"
                />
              </label>
              {deletion.error && (
                <div className="intelex-alert" role="alert">
                  {deletion.error}
                </div>
              )}
              <div>
                <button
                  type="button"
                  className="intelex-secondary-button"
                  onClick={() =>
                    setDeletion({
                      open: false,
                      reason: "",
                      working: false,
                      error: "",
                    })
                  }
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="intelex-danger-button"
                  disabled={deletion.working || !deletion.reason.trim()}
                >
                  {deletion.working ? "Deleting..." : "Delete submission"}
                </button>
              </div>
            </form>
          )}
          <section className="intelex-attempt-answers">
            <h3>Answers and scoring</h3>
            {detail.questions.map((question, index) => (
              <article key={question.id}>
                <div>
                  <span>{index + 1}</span>
                  <div>
                    <strong>{question.prompt}</strong>
                    <small>{question.type.replaceAll("_", " ")}</small>
                  </div>
                  <b>
                    {question.type === "written_response"
                      ? "Not scored"
                      : `${question.awardedPoints ?? "-"} / ${question.points}`}
                  </b>
                </div>
                <dl>
                  <dt>Applicant answer</dt>
                  <dd>{answerText(question)}</dd>
                  <dt>Correct key</dt>
                  <dd>
                    {question.type === "written_response"
                      ? "Manual review only"
                      : question.type === "short_text"
                        ? (question.correct.acceptedAnswers || []).join(" / ")
                        : question.type === "number"
                          ? `${question.correct.correctNumber} +/- ${question.correct.tolerance}`
                          : question.type === "true_false"
                            ? String(question.correct.correctBoolean)
                            : question.type === "matching"
                              ? (question.correct.pairs || [])
                                  .map(
                                    (pair) => `${pair.left} -> ${pair.right}`,
                                  )
                                  .join("; ")
                              : question.type === "ordering"
                                ? (question.correct.options || [])
                                    .map((option) => option.label)
                                    .join(" -> ")
                                : question.type === "single_choice"
                                  ? (question.correct.options || []).find(
                                      (option) =>
                                        option.id ===
                                        question.correct.correctOption,
                                    )?.label
                                  : (question.correct.options || [])
                                      .filter((option) =>
                                        (
                                          question.correct.correctOptions || []
                                        ).includes(option.id),
                                      )
                                      .map((option) => option.label)
                                      .join(", ")}
                  </dd>
                </dl>
              </article>
            ))}
          </section>
          <IntegrityTimeline
            events={detail.events}
            violationCount={detail.violationCount}
          />
        </div>
      ) : (
        <div className="intelex-alert">
          {state.error || "Attempt not found."}
        </div>
      )}
    </Modal>
  );
}

function AttemptsLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedAttempt = searchParams.get("attempt");
  const linkedUser = searchParams.get("user");
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    sort: "newest",
    page: 1,
  });
  const [attempts, setAttempts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [state, setState] = useState({ loading: true, error: "" });
  const [opened, setOpened] = useState(() => {
    if (
      /^[1-9]\d{0,18}$/.test(linkedUser || "") &&
      /^[1-9]\d{0,8}$/.test(linkedAttempt || "")
    )
      return { userId: linkedUser, attemptNumber: linkedAttempt };
    return /^[1-9]\d{0,18}$/.test(linkedAttempt || "")
      ? { legacyId: linkedAttempt }
      : null;
  });
  const setCanonicalAttempt = useCallback(
    (userId, attemptNumber) => {
      setOpened({ userId: String(userId), attemptNumber: String(attemptNumber) });
      const next = new URLSearchParams(searchParams);
      next.set("user", String(userId));
      next.set("attempt", String(attemptNumber));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const openAttempt = (attempt) => {
    setCanonicalAttempt(attempt.userId, attempt.attemptNumber);
  };
  const resolveAttempt = useCallback(
    (attempt) => {
      if (
        opened?.legacyId &&
        attempt?.userId &&
        attempt?.attemptNumber
      )
        setCanonicalAttempt(attempt.userId, attempt.attemptNumber);
    },
    [opened?.legacyId, setCanonicalAttempt],
  );
  const closeAttempt = () => {
    setOpened(null);
    const next = new URLSearchParams(searchParams);
    next.delete("user");
    next.delete("attempt");
    setSearchParams(next, { replace: true });
  };
  const query = useMemo(
    () =>
      new URLSearchParams(
        Object.entries(filters).filter(([, value]) => value !== ""),
      ).toString(),
    [filters],
  );
  const load = useCallback(async () => {
    try {
      const payload = await intelexRequest(
        `/api/portal/exams/admin/attempts?${query}`,
      );
      setAttempts(dataList(payload));
      setPagination(payload.pagination || { page: 1, totalPages: 1, total: 0 });
      setState({ loading: false, error: "" });
    } catch (error) {
      setState({ loading: false, error: error.message });
    }
  }, [query]);
  useEffect(() => {
    const timer = window.setTimeout(load, 180);
    return () => window.clearTimeout(timer);
  }, [load]);
  const set = (key, value) =>
    setFilters((current) => ({
      ...current,
      [key]: value,
      ...(key === "page" ? {} : { page: 1 }),
    }));
  return (
    <section className="intelex-panel intelex-exam-attempt-library">
      <div className="intelex-panel-heading">
        <div>
          <h2>Exam attempts</h2>
          <span>{pagination.total} recorded</span>
        </div>
      </div>
      <div className="intelex-report-library-filters">
        <label>
          <span>Search</span>
          <input
            type="search"
            value={filters.q}
            onChange={(event) => set("q", event.target.value)}
            placeholder="Applicant, email, exam or registration"
          />
        </label>
        <label>
          <span>Result</span>
          <select
            value={filters.status}
            onChange={(event) => set("status", event.target.value)}
          >
            <option value="">All results</option>
            <option value="in_progress">In progress</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
            <option value="timed_out">Timed out</option>
            <option value="void">Cancelled</option>
          </select>
        </label>
        <label>
          <span>Sort</span>
          <select
            value={filters.sort}
            onChange={(event) => set("sort", event.target.value)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="score">Highest score</option>
            <option value="violations">Most focus violations</option>
          </select>
        </label>
      </div>
      {state.error && <div className="intelex-alert">{state.error}</div>}
      {state.loading ? (
        <div className="intelex-loading">Loading exam attempts...</div>
      ) : attempts.length ? (
        <div className="intelex-table-scroll">
          <table className="intelex-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Exam</th>
                <th>Started</th>
                <th>Result</th>
                <th>Score</th>
                <th>Focus violations</th>
                <th>vAMSYS</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {attempts.map((attempt) => (
                <tr key={attempt.id}>
                  <td>
                    <strong>
                      {attempt.vamsysUsername ||
                        attempt.applicant ||
                        `User ${attempt.userId}`}
                    </strong>
                    <small className="intelex-table-note">
                      {attempt.vamsysUserId
                        ? `vAMSYS #${attempt.vamsysUserId} · Attempt #${attempt.attemptNumber}`
                        : `Local account · Attempt #${attempt.attemptNumber}`}
                    </small>
                  </td>
                  <td>{attempt.examTitle}</td>
                  <td>{displayDate(attempt.startedAt)}</td>
                  <td>
                    <span
                      className={`intelex-status intelex-status-${attempt.status}`}
                    >
                      {attempt.status === "void"
                        ? "cancelled"
                        : attempt.status.replace("_", " ")}
                    </span>
                  </td>
                  <td>
                    {attempt.percentage === null
                      ? "-"
                      : `${attempt.percentage}%`}
                  </td>
                  <td>{attempt.violationCount}</td>
                  <td>{attempt.approvalStatus.replace("_", " ")}</td>
                  <td>
                    <button
                      type="button"
                      className="intelex-table-link"
                      onClick={() => openAttempt(attempt)}
                    >
                      Inspect
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="intelex-empty">
          <strong>No attempts match these filters</strong>
        </div>
      )}
      <div className="intelex-library-pagination">
        <span>
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <div>
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => set("page", pagination.page - 1)}
          >
            <ArrowIcon direction="left" />
            Previous
          </button>
          <button
            type="button"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => set("page", pagination.page + 1)}
          >
            Next
            <ArrowIcon />
          </button>
        </div>
      </div>
      {opened && (
        <AttemptDetail
          reference={opened}
          onClose={closeAttempt}
          onChanged={load}
          onResolved={resolveAttempt}
        />
      )}
    </section>
  );
}

function AdminExams({ user }) {
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return /^[1-9]\d{0,18}$/.test(params.get("attempt") || "")
      ? "attempts"
      : "exams";
  });
  const [exams, setExams] = useState([]);
  const [editor, setEditor] = useState(null);
  const [state, setState] = useState({ loading: true, error: "", success: "" });
  const load = useCallback(async () => {
    try {
      setExams(dataList(await intelexRequest("/api/portal/exams/admin/exams")));
      setState((current) => ({ ...current, loading: false, error: "" }));
    } catch (error) {
      setState({ loading: false, error: error.message, success: "" });
    }
  }, []);
  useEffect(() => {
    if (user.role === "admin") load();
  }, [load, user.role]);
  const open = async (id) => {
    try {
      setEditor(
        normalizeExam(
          dataItem(await intelexRequest(`/api/portal/exams/admin/exams/${id}`)),
        ),
      );
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    }
  };
  const archive = async (exam) => {
    if (
      !(await confirmAction({
        title: "Archive exam",
        message: `Archive ${exam.title}? Applicants will no longer be able to start it.`,
        confirmLabel: "Archive exam",
        tone: "danger",
      }))
    )
      return;
    try {
      await intelexRequest(`/api/portal/exams/admin/exams/${exam.id}`, {
        method: "DELETE",
      });
      await load();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    }
  };
  if (user.role !== "admin")
    return (
      <div className="intelex-main">
        <div className="intelex-alert">Administrator access is required.</div>
      </div>
    );
  if (editor)
    return (
      <ExamEditor
        initial={editor}
        onCancel={() => setEditor(null)}
        onSaved={async () => {
          setEditor(null);
          await load();
          setState((current) => ({
            ...current,
            success: "Exam saved successfully.",
          }));
        }}
      />
    );
  return (
    <div className="intelex-main intelex-exam-admin">
      <header className="intelex-page-heading">
        <div>
          <h1>Exam system</h1>
          <p>Create secure entry assessments and inspect every attempt.</p>
        </div>
        {tab === "exams" && (
          <button
            type="button"
            className="intelex-primary-button"
            onClick={() => setEditor(freshExam())}
          >
            Create exam
          </button>
        )}
      </header>
      {state.error && <div className="intelex-alert">{state.error}</div>}
      {state.success && <div className="intelex-success">{state.success}</div>}
      <nav className="intelex-admin-library-tabs">
        <button
          type="button"
          className={tab === "exams" ? "active" : ""}
          onClick={() => setTab("exams")}
        >
          Exams <span>{exams.length}</span>
        </button>
        <button
          type="button"
          className={tab === "attempts" ? "active" : ""}
          onClick={() => setTab("attempts")}
        >
          Attempt records
        </button>
      </nav>
      {tab === "attempts" ? (
        <AttemptsLibrary />
      ) : (
        <section className="intelex-panel">
          <div className="intelex-panel-heading">
            <div>
              <h2>Assessments</h2>
              <span>Only one published entry exam can be active</span>
            </div>
          </div>
          {state.loading ? (
            <div className="intelex-loading">Loading exams...</div>
          ) : exams.length ? (
            <div className="intelex-exam-admin-list">
              {exams.map((exam) => (
                <article key={exam.id}>
                  <div>
                    <span>
                      {exam.isEntryExam ? "Entry assessment" : "Assessment"}
                    </span>
                    <h3>{exam.title}</h3>
                    <p>{exam.description || "No description"}</p>
                  </div>
                  <dl>
                    <div>
                      <dt>Status</dt>
                      <dd>{exam.status}</dd>
                    </div>
                    <div>
                      <dt>Revision</dt>
                      <dd>{exam.revision}</dd>
                    </div>
                    <div>
                      <dt>Pass grade</dt>
                      <dd>{exam.passingPercentage}%</dd>
                    </div>
                    <div>
                      <dt>Attempts</dt>
                      <dd>{exam.attemptCount}</dd>
                    </div>
                  </dl>
                  <div>
                    <button
                      type="button"
                      onClick={() => open(exam.id)}
                      disabled={exam.status === "archived"}
                    >
                      Edit
                    </button>
                    {exam.status !== "archived" && (
                      <button
                        type="button"
                        className="danger"
                        onClick={() => archive(exam)}
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="intelex-empty">
              <strong>No exams configured</strong>
              <p>Create the first entry assessment from a blank canvas.</p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default function IntelexExamAdminPage() {
  return (
    <IntelexFrame active="exams" title="Exam system">
      {({ user }) => <AdminExams user={user} />}
    </IntelexFrame>
  );
}
